import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import BaseTableHead from './BaseTableHead';
import BasePagination from '~/components/jobs/base-pagination/BasePagination';
import { DEFAULT_PAGE, DEFAULT_SORT_DIRECTION } from '~/utils/constants/jobs';
import styles from './BaseTable.module.scss';
import classNames from 'classnames/bind';
import BaseCheckbox from '../base-checkbox/BaseCheckbox';
import { SortDirection } from '~/utils/types/job';

const cx = classNames.bind(styles);

type BaseTableProps<T extends { id: string }> = {
  data: Array<T>;
  renderCell: (item: T, index: number) => React.ReactElement;
  searchInput: string;
  filterBy: Array<keyof T>;
  sortBy: Array<keyof T>;
  headCells: Array<{
    id: string;
    label: string;
    sortable: boolean;
  }>;
  totalPages: number;
  onChangePage?: (page: number) => void;
  onRequestSort?: (orderBy: string, order: SortDirection) => void;
  setSelectedIds: (selectedIds: Array<string>) => void;
};

const BaseTable = <T extends { id: string }>(props: BaseTableProps<T>) => {
  const [order, setOrder] = useState<SortDirection>(DEFAULT_SORT_DIRECTION);
  const [orderBy, setOrderBy] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(DEFAULT_PAGE);

  const { data, renderCell, headCells, setSelectedIds, onRequestSort, onChangePage, totalPages } = props;

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);

    if (onRequestSort && orderBy !== undefined) {
      onRequestSort(property, isAsc ? 'DESC' : 'ASC');
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map((item) => item.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.ChangeEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  useEffect(() => {
    setSelectedIds(selected);
  }, [selected, setSelectedIds]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (onChangePage) {
      onChangePage(newPage);
    }
  };

  return (
    <Box className={cx('table-container')}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <BaseTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy as string}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={data.length}
              headCells={headCells}
            />
            <TableBody>
              {data.map((item: T, index: number) => {
                return (
                  <TableRow
                    hover
                    role='checkbox'
                    tabIndex={-1}
                    key={item.id}
                    selected={isSelected(item.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding='checkbox' className='text-center'>
                      <BaseCheckbox
                        className='w-[20px] h-[20px]'
                        color='primary'
                        onChange={(event) => handleClick(event, item.id)}
                        checked={isSelected(item.id)}
                      />
                    </TableCell>
                    {renderCell(item, index)}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box className={cx('table-pagination-container')}>
          <BasePagination count={totalPages} page={page} onChange={handleChangePage} />
        </Box>
      </Paper>
    </Box>
  );
};

export default memo(BaseTable) as typeof BaseTable;
